import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { SortDirection } from '@angular/material/sort';
import { ParamMap, Router } from '@angular/router';

import { reloadOnCountChange } from '../../Shared/Services/reload-on-count-change.util';
import { CatalogOperationsFacade } from '../../../Facades/catalog-operations.facade';
import { DemandsListService } from '../Services/demands-list.service';
import {
  MyApprovalsListResponse,
  MyApprovalsListRow,
} from '../../../Interfaces/api/catalog/my-approvals-list.interface';
import { DemandSubjectType } from '../../../Enums/demand-subject-type.enum';
import { Platform } from '../../../Enums/platform.enum';
import { MergedApprovalRow, MyApprovalsFilters } from '../Interfaces/my-approvals.interface';
import {
  MY_APPROVALS_DEFAULT_DELEGATED,
  MY_APPROVALS_DEFAULT_ORDER,
  MY_APPROVALS_DEFAULT_ORDER_BY,
  MY_APPROVALS_DEFAULT_PAGE,
  MY_APPROVALS_DEFAULT_PAGE_SIZE,
  MY_APPROVALS_DEFAULT_PLATFORM,
  MY_APPROVALS_DEFAULT_REQUESTED_FROM,
  MY_APPROVALS_DEFAULT_REQUESTED_TO,
  MY_APPROVALS_DEFAULT_SUBJECT_TYPE,
  MY_APPROVALS_DEFAULT_TAB,
  MY_APPROVALS_ORDER_BY_VALUES,
  MY_APPROVALS_ORDER_VALUES,
  MY_APPROVALS_STATUS_FILTER_MAP,
  MY_APPROVALS_STATUS_FILTERS,
  MY_APPROVALS_SUBJECT_TYPES,
  MY_APPROVALS_TABS,
  MyApprovalsStatusFilter,
  MyApprovalsSubjectType,
  MyApprovalsTab,
} from '../Consts/list-my-approvals.const';
import { DEMANDS_SEARCH_MAX_LENGTH } from '../Consts/search-max-length.const';
import { ResourceStateService } from '../../Shared/Services/resource-state.service';
import { normalizeApprovalsStatusForTab } from '../Shared/approvals-status-for-tab.util';
import {
  parseBooleanParam,
  parseDateRangeParams,
  parseEnumParam,
  parseNullableEnumParam,
  parsePageParam,
  parseSearchParam,
} from '../Shared/query-param-parse.util';

@Injectable()
export class MyApprovalsFacade {
  private readonly _resourceState = inject(ResourceStateService);
  private readonly _demandsList = inject(DemandsListService);
  private readonly _catalogOperations = inject(CatalogOperationsFacade);
  private readonly _router = inject(Router);

  readonly filters = signal<MyApprovalsFilters>({
    tab: MY_APPROVALS_DEFAULT_TAB,
    status: 'all',
    search: '',
    page: MY_APPROVALS_DEFAULT_PAGE,
    pageSize: MY_APPROVALS_DEFAULT_PAGE_SIZE,
    orderBy: MY_APPROVALS_DEFAULT_ORDER_BY,
    order: MY_APPROVALS_DEFAULT_ORDER,
    requestedFrom: MY_APPROVALS_DEFAULT_REQUESTED_FROM,
    requestedTo: MY_APPROVALS_DEFAULT_REQUESTED_TO,
    platform: MY_APPROVALS_DEFAULT_PLATFORM,
    delegated: MY_APPROVALS_DEFAULT_DELEGATED,
  });

  readonly subjectType = signal<MyApprovalsSubjectType>(MY_APPROVALS_DEFAULT_SUBJECT_TYPE);

  readonly approvals = httpResource<MyApprovalsListResponse>(() =>
    this._demandsList.myApprovals({
      tab: this.filters().tab,
      status: MY_APPROVALS_STATUS_FILTER_MAP[this.filters().status],
      search: this.filters().search || undefined,
      page: this.filters().page,
      pageSize: this.filters().pageSize,
      orderBy: this.filters().orderBy,
      order: this.filters().order,
      requestedFrom: this.filters().requestedFrom ?? undefined,
      requestedTo: this.filters().requestedTo ?? undefined,
      platform: this.filters().platform ?? undefined,
      subjectType: this.subjectType(),
      delegated: this.filters().delegated,
    }),
  );

  readonly merged: Signal<MergedApprovalRow[]> = computed(() => {
    const rows = this._resourceState.valueOf(this.approvals)?.data?.data ?? [];
    return rows.map((row) => this._toMergedRow(row));
  });

  readonly totalCount: Signal<number> = computed(
    () => this._resourceState.valueOf(this.approvals)?.data?.count ?? 0,
  );

  readonly coldLoading: Signal<boolean> = computed(() => this.approvals.status() === 'loading');

  readonly loading: Signal<boolean> = computed(() => this.approvals.isLoading());

  readonly hasError: Signal<boolean> = computed(() => this.approvals.error() !== undefined);

  readonly activeFilterCount: Signal<number> = computed(() => {
    const f = this.filters();
    let count = 0;
    if (f.status !== 'all') count++;
    if (f.requestedFrom || f.requestedTo) count++;
    if (f.platform) count++;
    if (f.delegated) count++;
    if (this.subjectType() !== 'all') count++;
    return count;
  });

  readonly hasFilters: Signal<boolean> = computed(() => this.activeFilterCount() > 0);

  constructor() {
    this._registerCountRefresh();
  }

  private _registerCountRefresh(): void {
    reloadOnCountChange(
      () => this._catalogOperations.counts()?.my_approvals_awaiting,
      () => this.reload(),
    );
  }

  reload(): void {
    this.approvals.reload();
  }

  setFilters(partial: Partial<MyApprovalsFilters>): void {
    this.filters.update((current) => ({ ...current, ...partial }));
  }

  setTab(tab: MyApprovalsTab): void {
    if (tab === this.filters().tab) return;
    this.filters.update((current) => ({
      ...current,
      tab,
      status: normalizeApprovalsStatusForTab(tab, current.status),
      page: MY_APPROVALS_DEFAULT_PAGE,
    }));
    this._pushFiltersToUrl(false);
  }

  setStatus(status: MyApprovalsStatusFilter): void {
    this.filters.update((current) => ({ ...current, status, page: MY_APPROVALS_DEFAULT_PAGE }));
    this._pushFiltersToUrl(false);
  }

  setSearch(term: string): void {
    const search = term.trim();
    if (search === this.filters().search) return;
    this.filters.update((current) => ({ ...current, search, page: MY_APPROVALS_DEFAULT_PAGE }));
    this._pushFiltersToUrl(false);
  }

  setPage(page: number): void {
    this.filters.update((current) => ({ ...current, page }));
    this._pushFiltersToUrl(false);
  }

  setSort(orderBy: string, order: SortDirection): void {
    this.filters.update((current) => ({
      ...current,
      orderBy: order ? orderBy : MY_APPROVALS_DEFAULT_ORDER_BY,
      order: order || MY_APPROVALS_DEFAULT_ORDER,
      page: MY_APPROVALS_DEFAULT_PAGE,
    }));
    this._pushFiltersToUrl(false);
  }

  setPageSize(pageSize: number): void {
    this.filters.update((current) => ({ ...current, pageSize, page: MY_APPROVALS_DEFAULT_PAGE }));
    this._pushFiltersToUrl(false);
  }

  setSubjectType(subjectType: MyApprovalsSubjectType): void {
    this.subjectType.set(subjectType);
    this.filters.update((current) => ({ ...current, page: MY_APPROVALS_DEFAULT_PAGE }));
    this._pushFiltersToUrl(false);
  }

  setRequestedDateRange(requestedFrom: string | null, requestedTo: string | null): void {
    this.filters.update((current) => ({
      ...current,
      requestedFrom,
      requestedTo,
      page: MY_APPROVALS_DEFAULT_PAGE,
    }));
    this._pushFiltersToUrl(false);
  }

  setPlatform(platform: Platform | null): void {
    this.filters.update((current) => ({ ...current, platform, page: MY_APPROVALS_DEFAULT_PAGE }));
    this._pushFiltersToUrl(false);
  }

  setDelegated(delegated: boolean): void {
    this.filters.update((current) => ({ ...current, delegated, page: MY_APPROVALS_DEFAULT_PAGE }));
    this._pushFiltersToUrl(false);
  }

  clearFilters(): void {
    this.subjectType.set(MY_APPROVALS_DEFAULT_SUBJECT_TYPE);
    this.filters.update((current) => ({
      ...current,
      status: 'all',
      requestedFrom: MY_APPROVALS_DEFAULT_REQUESTED_FROM,
      requestedTo: MY_APPROVALS_DEFAULT_REQUESTED_TO,
      platform: MY_APPROVALS_DEFAULT_PLATFORM,
      delegated: MY_APPROVALS_DEFAULT_DELEGATED,
      page: MY_APPROVALS_DEFAULT_PAGE,
    }));
    this._pushFiltersToUrl(false);
  }

  syncFromQueryParams(params: ParamMap): boolean {
    const parsed = this._parseFiltersFromParams(params);
    return this._applyParsedFilters(parsed);
  }

  private _parseFiltersFromParams(params: ParamMap): {
    filters: MyApprovalsFilters;
    subjectType: MyApprovalsSubjectType;
    malformed: boolean;
  } {
    const tab = parseEnumParam(params.get('tab'), MY_APPROVALS_TABS, MY_APPROVALS_DEFAULT_TAB);
    const rawStatus = parseEnumParam(params.get('status'), MY_APPROVALS_STATUS_FILTERS, 'all');
    const status = {
      value: normalizeApprovalsStatusForTab(tab.value, rawStatus.value),
      malformed:
        rawStatus.malformed ||
        normalizeApprovalsStatusForTab(tab.value, rawStatus.value) !== rawStatus.value,
    };
    const dateRange = parseDateRangeParams(params.get('requestedFrom'), params.get('requestedTo'));
    const platform = parseNullableEnumParam(params.get('platform'), [
      Platform.Windows,
      Platform.Macos,
    ]);
    const page = parsePageParam(params.get('page'), MY_APPROVALS_DEFAULT_PAGE);
    const subjectType = parseEnumParam(
      params.get('subjectType'),
      MY_APPROVALS_SUBJECT_TYPES,
      MY_APPROVALS_DEFAULT_SUBJECT_TYPE,
    );
    const search = parseSearchParam(params.get('search'), DEMANDS_SEARCH_MAX_LENGTH);
    const delegated = parseBooleanParam(params.get('delegated'), MY_APPROVALS_DEFAULT_DELEGATED);
    const orderBy = parseEnumParam(
      params.get('orderBy'),
      MY_APPROVALS_ORDER_BY_VALUES,
      MY_APPROVALS_DEFAULT_ORDER_BY,
    );
    const order = parseEnumParam(
      params.get('order'),
      MY_APPROVALS_ORDER_VALUES,
      MY_APPROVALS_DEFAULT_ORDER,
    );

    const filters: MyApprovalsFilters = {
      ...this.filters(),
      tab: tab.value,
      status: status.value,
      search: search.value,
      requestedFrom: dateRange.value.from,
      requestedTo: dateRange.value.to,
      platform: platform.value,
      delegated: delegated.value,
      page: page.value,
      orderBy: orderBy.value,
      order: order.value,
    };

    const malformed =
      tab.malformed ||
      status.malformed ||
      search.malformed ||
      dateRange.malformed ||
      platform.malformed ||
      delegated.malformed ||
      page.malformed ||
      orderBy.malformed ||
      order.malformed ||
      subjectType.malformed;

    return { filters, subjectType: subjectType.value, malformed };
  }

  private _applyParsedFilters(parsed: {
    filters: MyApprovalsFilters;
    subjectType: MyApprovalsSubjectType;
    malformed: boolean;
  }): boolean {
    const filtersChanged = !this._filtersEqual(this.filters(), parsed.filters);
    const subjectTypeChanged = this.subjectType() !== parsed.subjectType;
    if (filtersChanged) {
      this.filters.set(parsed.filters);
    }
    if (subjectTypeChanged) {
      this.subjectType.set(parsed.subjectType);
    }
    if (parsed.malformed) {
      this._pushFiltersToUrl(true);
    }
    return filtersChanged || subjectTypeChanged;
  }

  private _filtersEqual(a: MyApprovalsFilters, b: MyApprovalsFilters): boolean {
    return (
      a.tab === b.tab &&
      a.status === b.status &&
      a.search === b.search &&
      a.requestedFrom === b.requestedFrom &&
      a.requestedTo === b.requestedTo &&
      a.platform === b.platform &&
      a.delegated === b.delegated &&
      a.page === b.page &&
      a.orderBy === b.orderBy &&
      a.order === b.order
    );
  }

  private _pushFiltersToUrl(replaceUrl: boolean): void {
    const f = this.filters();
    const subjectType = this.subjectType();
    const queryParams: Record<string, string | number | null> = {
      tab: f.tab === MY_APPROVALS_DEFAULT_TAB ? null : f.tab,
      subjectType: subjectType === MY_APPROVALS_DEFAULT_SUBJECT_TYPE ? null : subjectType,
      status: f.status === 'all' ? null : f.status,
      search: f.search.length > 0 ? f.search : null,
      requestedFrom: f.requestedFrom,
      requestedTo: f.requestedTo,
      platform: f.platform,
      delegated: f.delegated ? 'true' : null,
      page: f.page === MY_APPROVALS_DEFAULT_PAGE ? null : f.page,
      orderBy: f.orderBy === MY_APPROVALS_DEFAULT_ORDER_BY ? null : f.orderBy,
      order: f.order === MY_APPROVALS_DEFAULT_ORDER ? null : f.order,
    };
    void this._router.navigate(['/my-approvals'], {
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl,
    });
  }

  private _toMergedRow(row: MyApprovalsListRow): MergedApprovalRow {
    if (row.subject_type === DemandSubjectType.Application) {
      return { subjectType: DemandSubjectType.Application, row };
    }
    return { subjectType: DemandSubjectType.Group, row };
  }
}
